import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Download, FileText, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { renderAsync } from "docx-preview";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/Button";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// The resume pane of a review screen: the file's name and type, download and
// zoom, then the rendered document. The jump bar that used to sit in this
// header belongs to the form beside it — see ReviewFormPane in
// components/shared/SplitReviewLayout.jsx.
const ResumePreview = ({ file, onDownload }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const [fileType, setFileType] = useState("");
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(0.8);
  const [numPages, setNumPages] = useState(null);

  const docxRef = useRef(null);
  const createdPdfUrlRef = useRef(null);

  const getFileType = useCallback(() => {
    const mime = file?.type?.toLowerCase() || "";
    const name = file?.filename?.toLowerCase() || "";

    if (mime.includes("pdf") || name.endsWith(".pdf")) return "pdf";

    // Legacy binary .doc cannot be parsed by docx-preview — flag it as its own
    // type so the UI can say so instead of failing with a generic error.
    if (name.endsWith(".doc") && !name.endsWith(".docx")) return "doc";

    if (mime.includes("officedocument") || mime.includes("word") || name.endsWith(".docx")) {
      return "docx";
    }

    return "";
  }, [file]);

  const isDocx = useMemo(() => fileType === "docx", [fileType]);

  const clearDocx = useCallback(() => {
    if (docxRef.current) {
      docxRef.current.innerHTML = "";
    }
  }, []);

  const cleanupPdfUrl = useCallback(() => {
    if (createdPdfUrlRef.current) {
      URL.revokeObjectURL(createdPdfUrlRef.current);
      createdPdfUrlRef.current = null;
    }
  }, []);

  const renderDocx = useCallback(async (arrayBuffer) => {
    const container = docxRef.current;
    if (!container) {
      throw new Error("DOCX container not ready");
    }

    container.innerHTML = "";

    await renderAsync(arrayBuffer, container, null, {
      ignoreWidth: false,
      ignoreHeight: false,
      breakPages: true,
      useBase64URL: true,
      className: "docx",
      inWrapper: false,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFile = async () => {
      cleanupPdfUrl();
      clearDocx();
      setPdfUrl(null);
      setNumPages(null);
      setError(null);

      if (!file) {
        setFileType("");
        setLoading(false);
        return;
      }

      const detectedType = getFileType();
      setFileType(detectedType);
      setLoading(true);

      try {
        if (detectedType === "pdf") {
          let url = file.objectUrl || null;

          if (!url && file.blob) {
            url = URL.createObjectURL(file.blob);
            createdPdfUrlRef.current = url;
          }

          if (!url) {
            throw new Error("PDF URL not available");
          }

          if (!cancelled) {
            setPdfUrl(url);
          }
          return;
        }

        if (detectedType === "doc") {
          if (!cancelled) {
            setLoading(false);
          }
          return;
        }

        if (detectedType === "docx") {
          let arrayBuffer = null;

          if (file.blob) {
            arrayBuffer = await file.blob.arrayBuffer();
          } else if (file.objectUrl) {
            const res = await fetch(file.objectUrl);
            if (!res.ok) {
              throw new Error(`Failed to fetch DOCX blob URL: ${res.status}`);
            }
            arrayBuffer = await res.arrayBuffer();
          }

          if (!arrayBuffer) {
            throw new Error("DOCX data not available");
          }

          await renderDocx(arrayBuffer);

          if (!cancelled) {
            requestAnimationFrame(() => {
              if (!cancelled) {
                setLoading(false);
              }
            });
          }
          return;
        }

        if (!cancelled) {
          setError("Preview not available for this file type.");
          setLoading(false);
        }
      } catch (err) {
        console.error("ResumePreview loadFile error:", err);
        if (!cancelled) {
          setError("Failed to render document. Please try downloading instead.");
          setLoading(false);
        }
      }
    };

    loadFile();

    return () => {
      cancelled = true;
    };
  }, [file, getFileType, renderDocx, clearDocx, cleanupPdfUrl]);

  // Large resumes legitimately take a while to render. Instead of giving up
  // after 10s and permanently hiding the preview (the render does eventually
  // complete — nothing ever cleared that error), just soften the message and
  // keep waiting. The overlay goes away when rendering actually finishes.
  useEffect(() => {
    if (!loading) return;

    const timeout = setTimeout(() => setSlow(true), 10000);

    return () => {
      clearTimeout(timeout);
      setSlow(false);
    };
  }, [loading, file]);

  useEffect(() => {
    return () => {
      cleanupPdfUrl();
      clearDocx();
    };
  }, [cleanupPdfUrl, clearDocx]);

  const zoomOut = () => {
    setZoom((z) => Math.max(0.4, parseFloat((z - 0.1).toFixed(1))));
  };

  const zoomIn = () => {
    setZoom((z) => Math.min(2.0, parseFloat((z + 0.1).toFixed(1))));
  };

  return (
    <div className="flex flex-col h-full bg-background-secondary rounded-xl overflow-hidden border border-border-primary">
      <div className="z-10 flex items-center justify-between gap-2 px-3 py-2 bg-background-secondary border-b border-border-primary">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`p-1.5 rounded-lg shrink-0 ${
              fileType === "pdf"
                ? "bg-tone-rose-soft text-tone-rose"
                : fileType === "docx"
                  ? "bg-tone-blue-soft text-tone-blue"
                  : fileType === "doc"
                    ? "bg-tone-amber-soft text-tone-amber"
                    : "bg-background-tertiary text-text-muted"
            }`}
          >
            <FileText size={16} />
          </div>

          <span className="text-xs font-semibold text-text-primary truncate">{file?.filename || "Resume"}</span>
          <span className="text-[10px] uppercase font-bold tracking-wider text-text-muted shrink-0">{fileType || "—"}</span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {onDownload && (
            <Button
              onClick={onDownload}
              className="p-1.5 rounded-lg transition-colors text-text-secondary hover:bg-tone-blue-soft hover:text-tone-blue"
              title="Download resume"
              aria-label="Download resume"
            >
              <Download size={16} />
            </Button>
          )}

          <div className="flex items-center bg-background-tertiary rounded-full p-0.5 gap-0.5">
            <Button
              onClick={zoomOut}
              className="p-1 hover:bg-background-secondary rounded-full transition-colors text-text-primary"
              title="Zoom out"
              aria-label="Zoom out"
            >
              <ZoomOut size={14} />
            </Button>

            <span className="text-[11px] font-mono font-medium w-10 text-center text-text-primary">{Math.round(zoom * 100)}%</span>

            <Button onClick={zoomIn} className="p-1 hover:bg-background-secondary rounded-full transition-colors text-text-primary" title="Zoom in" aria-label="Zoom in">
              <ZoomIn size={14} />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar relative">
        {loading && (
          <div className="absolute inset-0 bg-background-secondary/90 flex flex-col items-center justify-center z-20">
            <Loader2 className="w-8 h-8 text-tone-blue animate-spin" />
            <p className="text-text-muted text-sm font-medium mt-2">{slow ? "This resume is taking a while to load…" : "Loading preview..."}</p>
          </div>
        )}

        {error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-8">
            <AlertCircle className="w-12 h-12 text-tone-rose" />
            <p className="text-text-primary font-semibold">Preview unavailable</p>
            <p className="text-text-secondary text-sm max-w-xs">{error}</p>
          </div>
        ) : (
          <div className="flex justify-center min-w-max" style={{ zoom }}>
            {fileType === "pdf" && pdfUrl && (
              <div className="flex flex-col items-center gap-6 mb-20">
                <Document
                  file={pdfUrl}
                  loading={null}
                  onLoadSuccess={({ numPages }) => {
                    setNumPages(numPages);
                  }}
                  onLoadError={(err) => {
                    console.error("PDF onLoadError:", err);
                    setError("Failed to load PDF.");
                    setLoading(false);
                  }}
                  onSourceError={(err) => {
                    console.error("PDF onSourceError:", err);
                    setError("Unable to read PDF source.");
                    setLoading(false);
                  }}
                >
                  {Array.from({ length: numPages || 0 }, (_, index) => (
                    <div key={index} className="mb-6 last:mb-0">
                      <Page
                        pageNumber={index + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={() => {
                          if (index === 0) {
                            setLoading(false);
                          }
                        }}
                        onRenderError={(err) => {
                          console.error("Page onRenderError:", err);
                          setError("Failed to render PDF page.");
                          setLoading(false);
                        }}
                      />
                    </div>
                  ))}
                </Document>
              </div>
            )}

            <div
              ref={docxRef}
              className="docx-outer"
              style={{
                display: isDocx ? "block" : "none",
                marginBottom: "5rem",
              }}
            />

            {fileType === "doc" && (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center py-16">
                <AlertCircle className="w-12 h-12 text-tone-amber" />
                <p className="text-text-primary font-semibold">Legacy .doc file</p>
                <p className="text-text-secondary text-sm max-w-xs">Older Word (.doc) files can't be previewed inline. Use the download button to view this resume.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        .docx-outer section.docx {
          background: #fff;
          box-shadow: 0 0 50px -12px rgba(0,0,0,0.15);
          border: 1px solid #e2e8f0;
          border-radius: 2px;
          margin: 0 auto 24px auto;
        }
      `}</style>
    </div>
  );
};

export default ResumePreview;
